import { FC, useEffect, useState } from 'react'
import { useIntl } from 'react-intl';
import { getAll } from '../../../../services/common_requests'
import { useQueryRequest } from '../core/QueryRequestProvider'
import { ImageDimensionHint } from '../../../../../_dashboard/partials/components/ImageDimensionHint'

interface ImageAddModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSave: (newImage: any) => void;
}

const ImageAddModal: React.FC<ImageAddModalProps> = ({ isOpen, onClose, onSave }) => {
    const [seoUrl, setSeoUrl] = useState('');
    const [link, setLink] = useState('');
    const [newImage, setNewImage] = useState<File | null>(null);
    const [previewImage, setPreviewImage] = useState('');
    const intl = useIntl()

    const LANGUAGE_NAMES: { [key: string]: string } = {
        en: intl.formatMessage({ id: 'COMMON.ENGLISH_LANGUAGE' }),
        fr: intl.formatMessage({ id: 'COMMON.FRENSH_LANGUAGE' }),
        es: intl.formatMessage({ id: 'COMMON.ESPANGOL_LANGUAGE' }),
        ar: intl.formatMessage({ id: 'COMMON.ARABIC_LANGUAGE' }),
      };
      const { state } = useQueryRequest();
      const [query, setQuery] = useState<any>(state);
      const [imageError, setImageError] = useState<string | null>(null);

      const [languages, setLanguages] = useState<{ code: string; name: string }[]>([]);
      const [selectedLanguage, setSelectedLanguage] = useState('en');
      const [translations, setTranslations] = useState<{ language: string; text: string; description: string; buttonText: string }[]>([]);
      useEffect(() => {
        if (languages.length > 0) {
          setSelectedLanguage(languages[0].code);
        }
      }, [languages]);
      useEffect(() => {
        getAll(query, '/languages').then((data) => {
          if (data && Array.isArray(data)) {
            const formattedLanguages = data.map((code: string) => ({
              code,
              name: LANGUAGE_NAMES[code] || code.toUpperCase(),
            }));
            setLanguages(formattedLanguages);
          }
        });
      }, [query]);

    const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            const file = e.target.files[0];
            setNewImage(file);
            setPreviewImage(URL.createObjectURL(file));
              }
    };
    const handleCancel = () => {
      resetForm();
      onClose(); 
    };
    const handleSave = () => {
        if (!newImage) {
          //alert(intl.formatMessage({ id: 'COMMON.ERROR.NO_IMAGE_SELECTED' }));
          setImageError(intl.formatMessage({ id: 'COMMON.ERROR.NO_IMAGE_SELECTED' }));

          return;
        }
        setImageError(null); 
        const formData = new FormData();
        formData.append('image', newImage);
        formData.append('seoUrl', seoUrl);
        formData.append('link', link);
        formData.append('translations[0][text]', translations[0]?.text);
        formData.append('translations[0][language]', translations[0]?.language);
        formData.append('translations[0][description]', translations[0]?.description);
        formData.append('translations[0][buttonText]', translations[0]?.buttonText);

        const newImageData = {
          url: newImage,
          seoUrl: seoUrl,
          link: link,
          translations: translations,    
        };
        resetForm();
        onSave(formData);
        onClose();
      };
      const resetForm = () => {
        setSeoUrl('');
        setLink(''); 
        setNewImage(null);
        setPreviewImage('');
        setTranslations([]);
        setImageError(null); 
      };
    
    if (!isOpen) return null;

    return (
        <div className="modal modal-style">
        <div className="modal-dialog">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">{intl.formatMessage({ id: 'COMMON.ADD_ITEMS' })}</h5>
              <button type="button" className="btn-close" onClick={handleCancel}></button>
            </div>
            <div className="modal-body">
              {previewImage && (
                <img src={previewImage} alt="Preview" className="modal-body-image" />
              )}
          <div className='fv-row mb-4'>
                <label className='required fw-bold fs-6 mb-2'>{intl.formatMessage({ id: 'COMMON.UPLOAD_IMAGE' })}</label>
                <input
                  type="file"
                  className="form-control"
                  accept="image/*"
                  onChange={handleImageChange}
                />
                <ImageDimensionHint entity='sliderHero' />
              {imageError && (
                <div className="text-danger mt-2">{imageError}</div>
              )}

              </div>
              <div className='fv-row mb-4'>
              <label className='fw-bold fs-6 mb-2'>
              {intl.formatMessage({ id: 'COMMON.ALT_IMAGE' })}</label>
                <input
                  type="text"
                  className="form-control"
                  value={seoUrl}
                  onChange={(e) => setSeoUrl(e.target.value)}
                />
              </div>

              <div className='fv-row mb-4'>
              <label className='fw-bold fs-6 mb-2'>
              {intl.formatMessage({ id: 'COLUMNS.LINK' })}</label>
              <input
                type="text"
                className="form-control"
                value={link}
                onChange={(e) => setLink(e.target.value)}
              />
            </div>

            {/* Language selector */}
            <div className='fv-row mb-4'>
            <label className='fw-bold fs-6 mb-2'>
              {intl.formatMessage({ id: 'COLUMNS.LANGUAGE' })}
            </label>
                <select
                  className="form-select"
                  value={selectedLanguage}
                  onChange={(e) => setSelectedLanguage(e.target.value)}
                >
                  {languages.map((lang) => (
                    <option key={lang.code} value={lang.code}>
                      {lang.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Add fields for text, description, and buttonText */}
              <div className='fv-row mb-4'>
              <label className='fw-bold fs-6 mb-2'>{intl.formatMessage({ id: 'COLUMNS.TEXT' })}</label>
                <input
                  type="text"
                  className="form-control"
                  value={translations.find(t => t.language === selectedLanguage)?.text || ''}
                  onChange={(e) => {
                    const updatedTranslations = translations.map(t =>
                      t.language === selectedLanguage ? { ...t, text: e.target.value } : t
                    );
                    if (!updatedTranslations.some(t => t.language === selectedLanguage)) {
                      updatedTranslations.push({ language: selectedLanguage, text: e.target.value, description: '', buttonText: '' });
                    }
                    setTranslations(updatedTranslations);
                  }}
                />
              </div>
              <div className='fv-row mb-4'>
              <label className='fw-bold fs-6 mb-2'>{intl.formatMessage({ id: 'COLUMNS.DESCRIPTION' })}</label>
                <input
                  type="text"
                  className="form-control"
                  value={translations.find(t => t.language === selectedLanguage)?.description || ''}
                  onChange={(e) => {
                    const updatedTranslations = translations.map(t =>
                      t.language === selectedLanguage ? { ...t, description: e.target.value } : t
                    );
                    if (!updatedTranslations.some(t => t.language === selectedLanguage)) {
                      updatedTranslations.push({ language: selectedLanguage, text: '', description: e.target.value, buttonText: '' });
                    }
                    setTranslations(updatedTranslations);
                  }}
                />
              </div>
              <div className='fv-row mb-4'>
              <label className='fw-bold fs-6 mb-2'>{intl.formatMessage({ id: 'COLUMNS.BUTTON_TEXT' })}</label>
                <input
                  type="text"
                  className="form-control"
                  value={translations.find(t => t.language === selectedLanguage)?.buttonText || ''}
                  onChange={(e) => {
                    const updatedTranslations = translations.map(t =>
                      t.language === selectedLanguage ? { ...t, buttonText: e.target.value } : t
                    );
                    if (!updatedTranslations.some(t => t.language === selectedLanguage)) {
                      updatedTranslations.push({ language: selectedLanguage, text: '', description: '', buttonText: e.target.value });
                    }
                    setTranslations(updatedTranslations);
                  }}
                />
              </div>

            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={handleCancel}>
                {intl.formatMessage({ id: 'HEADER.RESET' })}
              </button>
              <button type="button" className="btn btn-primary" onClick={handleSave}>
                {intl.formatMessage({ id: 'ACTION.ADD' })}
              </button>
            </div>
          </div>
        </div>
      </div>
        );
    
};

export default ImageAddModal;